import { PhoneMissed, Clock, UserX, PhoneCall } from 'lucide-react';

/**
 * "Every missed call is lost revenue" pain band. Static, reduced-motion-safe.
 * Frames the problem in a loss tone (rose accents on a soft tinted background,
 * matching the rest of the light marketing page) and ends on an emerald relief
 * line that hands off to the savings calculator. Stats are framed as industry
 * generalities, not fabricated first-party claims.
 */
const POINTS = [
  {
    icon: PhoneMissed,
    stat: '1 in 4',
    body: 'calls to small businesses go unanswered — most callers never call back.',
  },
  {
    icon: Clock,
    stat: 'After hours',
    body: 'is when many customers actually call. A voicemail box loses them by morning.',
  },
  {
    icon: UserX,
    stat: 'They move on',
    body: 'to the next result. A missed call is usually a customer won by a competitor.',
  },
];

export function MissedCallPain(): JSX.Element {
  return (
    <section className="relative overflow-hidden border-y border-border bg-gradient-to-b from-rose-50/60 to-background py-14 dark:from-rose-950/20 sm:py-20">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-rose-500/10 px-3 py-1 text-sm font-medium text-rose-600 dark:text-rose-400">
            <PhoneMissed className="h-4 w-4" aria-hidden="true" />
            The cost of silence
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Every missed call is lost revenue
          </h2>
          <p className="mt-4 text-muted-foreground">
            You can&apos;t answer every call and message yourself — and missing them
            quietly drains your pipeline.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-3">
          {POINTS.map(({ icon: Icon, stat, body }) => (
            <div
              key={stat}
              className="rounded-2xl border border-rose-200/60 bg-card p-6 shadow-sm dark:border-rose-900/40"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <p className="mt-4 text-2xl font-bold tracking-tight">{stat}</p>
              <p className="mt-1 text-sm text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-10 flex max-w-2xl items-center justify-center gap-2 text-center text-sm font-medium text-emerald-600 dark:text-emerald-400">
          <PhoneCall className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>Your AI receptionist answers every one — voice and chat, 24/7/365.</span>
        </div>
      </div>
    </section>
  );
}
